import { IHotkeyModifiers, IHotkeyBinding, HotkeyEventType } from './hotkey.models';

/** Special keys that should be case-insensitive and preserve their original casing */
const SPECIAL_KEYS = Object.freeze([
    'Enter',
    'Escape',
    'Tab',
    'Space',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Backspace',
    'Delete',
    'Home',
    'End',
    'PageUp',
    'PageDown',
    'Insert',
    'F1',
    'F2',
    'F3',
    'F4',
    'F5',
    'F6',
    'F7',
    'F8',
    'F9',
    'F10',
    'F11',
    'F12'
]);

/**
 * Normalizes a key string to a consistent format.
 * Converts to lowercase and handles special key names
 * @param key The key string to normalize
 * @returns The normalized key string
 */
export function normalizeKey(key: string): string {
    if (!key) {
        return '';
    }

    // event.key for the spacebar is " " (U+0020); trim() turns it into "" and we lose the key.
    // Handle it before trim so it normalizes to "Space" and matches bindings using 'Space'.
    if (key === ' ' || key === '\u0020') {
        return 'Space';
    }

    const normalized = key.trim();

    return SPECIAL_KEYS.find(
        sk => sk.toLowerCase() === normalized.toLowerCase()
    ) ?? normalized.toLowerCase();
}

/**
 * Creates a normalized key combination string for comparison.
 * Format: "modifiers:key" (e.g., "shift+ctrl:s", "alt+meta:Enter")
 * @param key The main key
 * @param modifiers Optional modifier keys
 * @returns A normalized string representation of the key combination
 */
// eslint-disable-next-line complexity
export function createNormalizedKeyString(
    key: string,
    modifiers?: IHotkeyModifiers
): string {
    const normalizedKey = normalizeKey(key);
    const modifierParts: string[] = [];

    if (modifiers?.shift) {
        modifierParts.push('shift');
    }
    if (modifiers?.ctrl) {
        modifierParts.push('ctrl');
    }
    if (modifiers?.alt) {
        modifierParts.push('alt');
    }
    if (modifiers?.meta) {
        modifierParts.push('meta');
    }

    const modifierString = modifierParts.length > 0
        ? modifierParts.sort().join('+') + ':'
        : '';

    return `${modifierString}${normalizedKey}`;
}

/**
 * Creates a hotkey binding with normalized key calculated automatically
 * @param id Unique identifier for this binding
 * @param key The main key (e.g., 's', 'Enter', 'Escape')
 * @param modifiers Optional modifier keys
 * @param eventType Optional event type (defaults to KEYDOWN)
 * @param enabled Optional enabled state (defaults to true)
 * @returns A complete IHotkeyBinding with normalizedKey calculated
 */
export function createHotkeyBinding(
    id: string,
    key: string,
    eventType: HotkeyEventType,
    modifiers?: IHotkeyModifiers,
    enabled?: boolean
): IHotkeyBinding {
    const normalizedKey = createNormalizedKeyString(key, modifiers);

    return {
        id,
        key,
        modifiers,
        eventType,
        enabled: enabled ?? true,
        normalizedKey
    };
}
